/**
 * 国际化入口文件
 *
 * 使用方式：
 *   import { t } from '@/lang'
 *   const text = t('toolbar.undo')  // => 'Undo'
 *
 * 支持嵌套 key：
 *   t('common.confirm')  // => 'Confirm'
 *   t('designCenter.useTemplate')  // => 'Use a template'
 *
 * 支持 Lang_Pack 路径格式：
 *   t('pages/designCenter.pageTitle')  // => 从 Lang_Pack 查找
 *
 * 查找策略：
 *   - key 包含 `/` → 从 Lang_Pack 查找（以最后一个 `.` 分割为模块路径和翻译 key）
 *   - key 不包含 `/` → 优先从 Lang_Pack 链式查找，失败后从本地语言文件兜底
 *   - 两种情况下未找到均返回 fallback 或 key 本身
 *
 * 响应式说明：
 *   t() 内部访问 Pinia store 的 langPacks 状态，在 Vue 组件的渲染上下文中调用时
 *   会自动建立响应式依赖。当语言包更新时，使用了 t() 的模板会自动重新渲染。
 *   推荐在组件中使用 useI18n() composable 获取 t 函数以获得更好的类型提示和明确的响应式追踪。
 *
 * 注意：本项目使用内部插件进行国际化，不依赖 vue-i18n
 */
import en from './en'
import { useLocaleStore } from '@/store/locale'

// 语言包类型（支持嵌套对象）
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LangMessages = Record<string, any>

// 当前支持的语言
const messages: Record<string, LangMessages> = {
  en,
}

// 默认语言（后续可从 store/cookie 中动态获取）
let currentLang = 'en'

/**
 * 设置当前语言
 */
export function setLang(lang: string) {
  if (messages[lang]) {
    currentLang = lang
  } else {
    console.warn(`[i18n] Language "${lang}" is not supported.`)
  }
}

/**
 * 获取当前语言
 */
export function getLang(): string {
  return currentLang
}

/**
 * 安全获取 locale store 实例
 * Pinia 可能尚未初始化（模块加载阶段），需要 try-catch 保护
 */
function getLocaleStore() {
  try {
    return useLocaleStore()
  } catch {
    return null
  }
}

/**
 * 从 Lang_Pack 中查找翻译
 * key 格式: "pages/designCenter.pageTitle" → 模块路径 "pages/designCenter", 翻译 key "pageTitle"
 * 以最后一个 `.` 分割
 */
function lookupLangPack(key: string): string | undefined {
  const localeStore = getLocaleStore()
  if (!localeStore) return undefined

  const lastDotIndex = key.lastIndexOf('.')
  if (lastDotIndex === -1) return undefined

  const modulePath = key.slice(0, lastDotIndex)
  const translationKey = key.slice(lastDotIndex + 1)

  const moduleData = localeStore.langPacks[modulePath]
  if (!moduleData) return undefined

  return moduleData[translationKey]
}

/**
 * 从本地语言文件中查找翻译
 * key 格式: "toolbar.undo" → 模块 "toolbar", 字段 "undo"
 *           "operationManual.common.title" → 模块 "operationManual", 链式查找 common → title
 * 以第一个 `.` 分割模块名，剩余部分支持链式深层访问
 */
function lookupLocalFiles(key: string): string | undefined {
  const pack = messages[currentLang]
  if (!pack) return undefined

  const [module, ...rest] = key.split('.')
  if (rest.length === 0) return undefined

  const moduleMessages = pack[module]
  if (!moduleMessages) return undefined

  // 先尝试扁平 key 查找（兼容旧格式，如 key = "toolbar.undo" → moduleMessages["undo"]）
  const flatKey = rest.join('.')
  const flatResult = moduleMessages[flatKey]
  if (typeof flatResult === 'string') return flatResult

  // 链式深层查找（如 key = "operationManual.common.title" → moduleMessages.common.title）
  let current: unknown = moduleMessages
  for (const segment of rest) {
    if (current === null || current === undefined || typeof current !== 'object') return undefined
    current = (current as Record<string, unknown>)[segment]
  }

  return typeof current === 'string' ? current : undefined
}

/**
 * 从 Lang_Pack 中通过链式路径查找翻译
 * key 格式: "operationManual.common.title" → langPacks 中逐层查找
 */
function lookupLangPackChain(key: string): string | undefined {
  const localeStore = getLocaleStore()
  if (!localeStore) return undefined

  const segments = key.split('.')
  if (segments.length < 2) return undefined

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let current: any = localeStore.langPacks
  for (const segment of segments) {
    if (current === null || current === undefined || typeof current !== 'object') return undefined
    current = current[segment]
  }

  return typeof current === 'string' ? current : undefined
}

/**
 * 翻译函数
 * @param key - 翻译 key
 *   - 包含 `/` 时为 Lang_Pack 路径格式，如 "pages/designCenter.pageTitle"
 *   - 不包含 `/` 时为本地文件格式，支持链式深层访问：
 *     - "toolbar.undo" → 模块 toolbar, 字段 undo
 *     - "operationManual.common.title" → 模块 operationManual, 链式 common → title
 * @param fallback - 未找到翻译时的回退文案（可选，默认返回 key 本身）
 * @returns 翻译后的文案
 *
 * @example
 * t('toolbar.undo')                         // => 'Undo' (扁平 key)
 * t('operationManual.common.title')         // => 'FS Design Center' (链式深层)
 * t('operationManual.overview.aboutTitle')  // => 'About Design Center' (链式深层)
 * t('pages/designCenter.pageTitle')         // => 从 Lang_Pack 查找
 * t('unknown.key', '默认值')                 // => '默认值'
 */
export function t(key: string, fallback?: string): string {
  if (key.includes('/')) {
    // Lang_Pack 路径格式：优先从 Lang_Pack 查找，失败则回退到本地文件
    const langPackResult = lookupLangPack(key)
    if (langPackResult !== undefined) return langPackResult

    // 回退到本地文件查找
    // Lang_Pack 路径如 "components/layout.sidebarTopology" 需要转换为本地格式 "layout.sidebarTopology"
    const lastDotIndex = key.lastIndexOf('.')
    if (lastDotIndex !== -1) {
      const modulePath = key.slice(0, lastDotIndex)
      const translationKey = key.slice(lastDotIndex + 1)
      // 取路径最后一段作为本地模块名（如 "components/layout" → "layout"）
      const lastSlashIndex = modulePath.lastIndexOf('/')
      const localModule = lastSlashIndex !== -1 ? modulePath.slice(lastSlashIndex + 1) : modulePath
      const localKey = `${localModule}.${translationKey}`
      const localResult = lookupLocalFiles(localKey)
      if (localResult !== undefined) return localResult
    }

    return fallback ?? key
  }

  // 优先从 Lang_Pack 链式查找（支持 operationManual.common.title 格式）
  const langPackChainResult = lookupLangPackChain(key)
  if (langPackChainResult !== undefined) return langPackChainResult

  // 本地语言文件作为兜底（支持链式深层访问）
  const localResult = lookupLocalFiles(key)
  if (localResult !== undefined) return localResult

  return fallback ?? key
}

// 导出语言包供外部使用
export { en }
export { useI18n } from '@/hooks/useI18n'
export default { t, setLang, getLang }
